/**
 * Send Proposal Button - Phase 9.17 Enhanced Email Confirmation
 * Updated Phase 10a.1: Added Edit/Preview toggle with email body preview
 * Updated Phase 10a.2: Ref-based sliding pill + PDF preview tab
 *
 * Sends proposal via email with PDF attachment.
 * Shows full confirmation dialog with:
 * - Recipient email (editable)
 * - BCC list from settings (editable for this send)
 * - Reply-To (editable if not configured in settings)
 * - Subject preview (read-only)
 * - Attachment filename (read-only)
 * - Personal note (optional)
 * - Email body preview (toggle)
 * - PDF attachment preview (toggle)
 */
import { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { invalidateProjectListQueries } from '@/lib/cache-utils';
import { Mail, Send, Loader2, Paperclip, Reply, Users, Eye, Pencil, Lock, FileText, AlertCircle, History } from 'lucide-react';
import { useEmailSettings, useEmailPreview } from '@/hooks';
import { EmailPreviewPanel } from '@/components/email/EmailPreviewPanel';
import { EmailSendHistory } from '@/components/email/EmailSendHistory';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/components/ui/tooltip';

interface SendProposalButtonProps {
  projectId: string;
  proposalNumber?: string;
  projectName?: string;
  defaultRecipient?: string;
  disabled?: boolean;
  /** Tab to open the dialog on (defaults to 'edit') */
  initialTab?: 'edit' | 'preview' | 'pdf' | 'history';
  /** Controlled open state - when provided, the component doesn't render its own trigger button */
  open?: boolean;
  /** Controlled open change handler */
  onOpenChange?: (open: boolean) => void;
  /** When true, hide the trigger button (dialog-only mode for overflow menu integration) */
  hideButton?: boolean;
}

export function SendProposalButton({
  projectId,
  proposalNumber,
  projectName,
  defaultRecipient = '',
  disabled = false,
  initialTab,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  hideButton = false,
}: SendProposalButtonProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? (controlledOnOpenChange ?? (() => {})) : setInternalOpen;
  const [recipient, setRecipient] = useState(defaultRecipient);
  const [bccList, setBccList] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [customNote, setCustomNote] = useState('');
  const [activeView, setActiveView] = useState<'edit' | 'preview' | 'pdf' | 'history'>(initialTab ?? 'edit');
  const [fading, setFading] = useState(false);
  const queryClient = useQueryClient();

  // Refs for segmented control buttons
  const editBtnRef = useRef<HTMLButtonElement>(null);
  const emailBtnRef = useRef<HTMLButtonElement>(null);
  const pdfBtnRef = useRef<HTMLButtonElement>(null);
  const historyBtnRef = useRef<HTMLButtonElement>(null);
  const [pillStyle, setPillStyle] = useState({ left: 0, width: 0 });

  // Measure active tab for sliding pill
  const measurePill = useCallback(() => {
    const refMap = { edit: editBtnRef, preview: emailBtnRef, pdf: pdfBtnRef, history: historyBtnRef };
    const ref = refMap[activeView];
    const btn = ref.current;
    if (btn) {
      setPillStyle({ left: btn.offsetLeft, width: btn.offsetWidth });
    }
  }, [activeView]);

  useLayoutEffect(() => {
    measurePill();
  }, [measurePill]);

  // Sync activeView with initialTab when dialog opens (render-time adjustment, no flash)
  const [prevOpen, setPrevOpen] = useState(open);
  if (open && !prevOpen) {
    setPrevOpen(true);
    if (initialTab) {
      setActiveView(initialTab);
    }
  } else if (!open && prevOpen) {
    setPrevOpen(false);
  }

  // PDF preview state
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<Error | null>(null);
  const [pdfRetryCount, setPdfRetryCount] = useState(0);

  // Fetch email settings
  const { data: emailSettings, isLoading: settingsLoading } = useEmailSettings();

  // Email preview (only fetches when in preview mode)
  const {
    html: previewHtml,
    isLoading: previewLoading,
    error: previewError,
    refetch: refetchPreview,
  } = useEmailPreview('proposal', projectId, customNote, activeView === 'preview');

  // Fetch PDF blob when switching to PDF tab
  useEffect(() => {
    if (activeView !== 'pdf') return;
    let cancelled = false;
    setPdfLoading(true);
    setPdfError(null);

    api.projects.downloadProposalPDF(projectId)
      .then((blob) => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        setPdfBlobUrl(url);
        setPdfLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setPdfError(err instanceof Error ? err : new Error(String(err)));
        setPdfLoading(false);
      });

    return () => { cancelled = true; };
  }, [activeView, projectId, pdfRetryCount]);

  // Clean up blob URL on unmount or when URL changes
  useEffect(() => {
    return () => {
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    };
  }, [pdfBlobUrl]);

  // Initialize form with settings when modal opens
  useEffect(() => {
    if (open && emailSettings) {
      setBccList(emailSettings.bcc_list?.join(', ') || '');
      setReplyTo(emailSettings.reply_to || '');
    }
    if (open && defaultRecipient) {
      setRecipient(defaultRecipient);
    }
  }, [open, emailSettings, defaultRecipient]);

  const sendMutation = useMutation({
    mutationFn: () => {
      // Parse BCC list
      const bccArray = bccList
        .split(',')
        .map((e) => e.trim())
        .filter(Boolean);

      return api.post(`/api/projects/${projectId}/send-proposal-email`, {
        recipient,
        custom_note: customNote || null,
        bcc_override: bccArray,
        reply_to_override: replyTo || null,
      });
    },
    onSuccess: () => {
      toast.success(`Proposal sent to ${recipient}`);
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', 'detail', projectId] });
      invalidateProjectListQueries(queryClient);
      queryClient.invalidateQueries({ queryKey: ['email-send-history', 'proposal', projectId] });
      setOpen(false);
      setCustomNote('');
      setActiveView(initialTab ?? 'edit');
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.detail ||
        error?.message ||
        'Failed to send proposal';
      toast.error(message);
    },
  });

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      // Reset form when closing
      setCustomNote('');
      setActiveView(initialTab ?? 'edit');
      setFading(false);
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
        setPdfBlobUrl(null);
      }
      setPdfError(null);
    }
  };

  // Smooth cross-fade between views
  const switchView = (newView: 'edit' | 'preview' | 'pdf' | 'history') => {
    if (newView === activeView || fading) return;
    setFading(true);
    setTimeout(() => {
      setActiveView(newView);
      setFading(false);
    }, 200);
  };

  const displayNumber = proposalNumber || projectName || `P-${projectId.slice(0, 8)}`;
  const subject = `Proposal for ${projectName || 'Project'}`;
  const attachmentName = `Proposal-${displayNumber.replace(/[^a-zA-Z0-9_-]/g, '_')}.pdf`;

  // Validation
  const canSend = recipient && replyTo;
  const replyToFromSettings = Boolean(emailSettings?.reply_to);

  return (
    <>
      {!hideButton && (
        <Button
          onClick={() => handleOpenChange(true)}
          disabled={disabled}
          variant="default"
          data-testid="send-proposal-button"
        >
          <Mail className="w-4 h-4 mr-2" />
          Send Proposal
        </Button>
      )}

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className={`max-h-[90vh] !flex !flex-col transition-[max-width] duration-300 ${
            activeView === 'pdf'
              ? 'sm:max-w-[95vw] xl:max-w-[1200px]'
              : activeView === 'preview'
                ? 'sm:max-w-[700px]'
                : activeView === 'history'
                  ? 'sm:max-w-[600px]'
                  : 'sm:max-w-[540px]'
          }`}
        >
          <DialogHeader>
            <DialogTitle className="truncate">Send Proposal{projectName ? ` — ${projectName}` : ` ${displayNumber}`}</DialogTitle>
            <DialogDescription>
              Review and confirm email details before sending.
            </DialogDescription>
          </DialogHeader>

          {/* Edit / Email / PDF Segmented Control with sliding pill */}
          <div className="flex items-center">
            <div className="bg-muted rounded-lg p-1 relative flex gap-0.5">
              {/* Sliding pill background */}
              {pillStyle.width > 0 && (
                <div
                  className="absolute top-1 bottom-1 rounded-md bg-background shadow-sm transition-all duration-200 ease-in-out"
                  style={{
                    width: pillStyle.width,
                    transform: `translateX(${pillStyle.left}px)`,
                    left: 0,
                  }}
                />
              )}
              <button
                ref={editBtnRef}
                className={`relative z-10 inline-flex items-center rounded-md px-3 py-1.5 text-sm font-medium transition-colors duration-200 ${
                  activeView === 'edit'
                    ? 'text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => switchView('edit')}
                data-testid="edit-toggle"
              >
                <Pencil className="w-3.5 h-3.5 mr-1.5" />
                Edit
              </button>
              <button
                ref={emailBtnRef}
                className={`relative z-10 inline-flex items-center rounded-md px-3 py-1.5 text-sm font-medium transition-colors duration-200 ${
                  activeView === 'preview'
                    ? 'text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => switchView('preview')}
                data-testid="preview-toggle"
              >
                <Eye className="w-3.5 h-3.5 mr-1.5" />
                Email
              </button>
              <button
                ref={pdfBtnRef}
                className={`relative z-10 inline-flex items-center rounded-md px-3 py-1.5 text-sm font-medium transition-colors duration-200 ${
                  activeView === 'pdf'
                    ? 'text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => switchView('pdf')}
                data-testid="pdf-toggle"
              >
                <FileText className="w-3.5 h-3.5 mr-1.5" />
                PDF
              </button>
              <button
                ref={historyBtnRef}
                className={`relative z-10 inline-flex items-center rounded-md px-3 py-1.5 text-sm font-medium transition-colors duration-200 ${
                  activeView === 'history'
                    ? 'text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => switchView('history')}
                data-testid="history-toggle"
              >
                <History className="w-3.5 h-3.5 mr-1.5" />
                History
              </button>
            </div>
          </div>

          {/* Scrollable content area */}
          <div className={`overflow-y-auto scrollbar-thin flex-1 min-h-0 -mx-6 px-6 transition-opacity duration-200 ease-in-out ${fading ? 'opacity-0' : 'opacity-100'}`}>
            {settingsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                {/* Subject + Attachment metadata card */}
                <div className="bg-muted/50 rounded-lg px-3 py-2.5 mt-3">
                  <div className="flex items-start gap-3 text-sm">
                    <span className="text-muted-foreground w-20 shrink-0">Subject:</span>
                    <span className="font-medium">{subject}</span>
                  </div>
                  <div className="flex items-start gap-3 text-sm mt-2">
                    <span className="text-muted-foreground w-20 shrink-0">Attachment:</span>
                    <span className="flex items-center gap-1.5">
                      <Paperclip className="w-3.5 h-3.5" />
                      {attachmentName}
                    </span>
                  </div>
                </div>

                {activeView === 'edit' ? (
                  <div className="space-y-4 py-2">
                    {/* Recipient */}
                    <div>
                      <Label htmlFor="recipient" className="text-sm font-medium">
                        To <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="recipient"
                        type="email"
                        value={recipient}
                        onChange={(e) => setRecipient(e.target.value)}
                        placeholder="client@example.com"
                        className="mt-1.5"
                        data-testid="recipient-input"
                      />
                    </div>

                    {/* BCC */}
                    <div>
                      <Label htmlFor="bcc" className="text-sm font-medium flex items-center gap-1.5">
                        <Users className="w-3.5 h-3.5" />
                        BCC (team copies)
                      </Label>
                      <Input
                        id="bcc"
                        type="text"
                        value={bccList}
                        onChange={(e) => setBccList(e.target.value)}
                        placeholder="accounting@company.com, admin@company.com"
                        className="mt-1.5"
                        data-testid="bcc-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Comma-separated. {emailSettings?.bcc_list?.length ? 'Defaults from Settings.' : 'No defaults configured.'} Edit for this send only.
                      </p>
                    </div>

                    {/* Reply-To */}
                    <div>
                      <Label htmlFor="reply-to" className="text-sm font-medium flex items-center gap-1.5">
                        <Reply className="w-3.5 h-3.5" />
                        Reply-To <span className="text-red-500">*</span>
                      </Label>
                      {replyToFromSettings ? (
                        <div className="mt-1.5 px-3 py-2 bg-muted rounded-md text-sm flex items-center justify-between">
                          <span>{replyTo}</span>
                          <Lock className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        </div>
                      ) : (
                        <Input
                          id="reply-to"
                          type="email"
                          value={replyTo}
                          onChange={(e) => setReplyTo(e.target.value)}
                          placeholder="proposals@yourcompany.com"
                          className="mt-1.5"
                          data-testid="reply-to-input"
                        />
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {replyToFromSettings
                          ? 'Configured in Settings > Email. Client replies go here.'
                          : 'Required. Where client replies will go.'}
                      </p>
                    </div>

                    {/* Custom Note */}
                    <div className="border-t pt-4">
                      <Label htmlFor="note" className="text-sm font-medium">
                        Personal Note (optional)
                      </Label>
                      <Textarea
                        id="note"
                        value={customNote}
                        onChange={(e) => setCustomNote(e.target.value)}
                        placeholder="Add a personal message that will appear in the email body..."
                        rows={4}
                        className="mt-1.5"
                        data-testid="custom-note-input"
                      />
                    </div>
                  </div>
                ) : activeView === 'preview' ? (
                  /* Email Preview View */
                  <div className="py-2">
                    <EmailPreviewPanel
                      html={previewHtml}
                      isLoading={previewLoading}
                      error={previewError}
                      onRetry={() => refetchPreview()}
                    />
                  </div>
                ) : activeView === 'history' ? (
                  /* Send History View (Phase 10a.3) */
                  <div className="py-2">
                    <EmailSendHistory entityType="proposal" entityId={projectId} />
                  </div>
                ) : (
                  /* PDF Preview View */
                  <div className="py-2">
                    <div className="rounded-lg border bg-muted/30 p-4 animate-in fade-in-0 duration-200" data-testid="pdf-preview-panel">
                      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-3">
                        <FileText className="w-4 h-4" />
                        PDF Attachment Preview
                      </div>
                      {pdfLoading ? (
                        <div className="flex flex-col items-center justify-center py-12 gap-3">
                          <Loader2
                            className="w-6 h-6 animate-spin text-muted-foreground"
                            data-testid="pdf-preview-loading"
                          />
                          <p className="text-sm text-muted-foreground">Loading PDF...</p>
                        </div>
                      ) : pdfError ? (
                        <div className="flex flex-col items-center justify-center py-8 gap-3">
                          <AlertCircle className="w-6 h-6 text-destructive" />
                          <p className="text-sm text-destructive">Failed to load PDF preview</p>
                          <p className="text-xs text-muted-foreground">{pdfError.message}</p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPdfRetryCount((c) => c + 1)}
                            data-testid="pdf-preview-retry"
                          >
                            Retry
                          </Button>
                        </div>
                      ) : pdfBlobUrl ? (
                        <>
                          {/* Desktop: inline PDF viewer */}
                          <div className="hidden sm:block rounded border bg-white overflow-hidden">
                            <embed
                              src={`${pdfBlobUrl}#navpanes=0&view=FitH`}
                              type="application/pdf"
                              className="w-full border-0"
                              style={{ height: '70vh' }}
                              data-testid="pdf-preview-iframe"
                            />
                          </div>
                          {/* Mobile: download/open link (PDF iframes don't work well on mobile) */}
                          <div className="sm:hidden flex flex-col items-center gap-3 py-8">
                            <FileText className="w-10 h-10 text-muted-foreground" />
                            <p className="text-sm text-muted-foreground text-center">
                              PDF preview is best viewed on desktop.
                            </p>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(pdfBlobUrl, '_blank')}
                              data-testid="pdf-open-mobile"
                            >
                              Open PDF
                            </Button>
                          </div>
                        </>
                      ) : null}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Sticky footer with actions */}
          <div className="flex items-center justify-end gap-2 border-t pt-3 mt-auto pb-[env(safe-area-inset-bottom)]">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)} className="max-sm:flex-1">
              Cancel
            </Button>
            <div className="max-sm:flex-1">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                    <Button
                      size="sm"
                      onClick={() => sendMutation.mutate()}
                      disabled={!canSend || sendMutation.isPending || settingsLoading}
                      className="disabled:opacity-40 w-full"
                      data-testid="send-email-button"
                    >
                      {sendMutation.isPending ? (
                        <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4 mr-1.5" />
                      )}
                      {sendMutation.isPending ? 'Sending...' : 'Send Email'}
                    </Button>
                    </span>
                  </TooltipTrigger>
                  {!canSend && (
                    <TooltipContent>
                      {!recipient && !replyTo
                        ? 'Enter a recipient and reply-to address'
                        : !recipient
                          ? 'Enter a recipient email address'
                          : 'Enter a reply-to email address'}
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
